import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Mic, Sparkles } from "lucide-react";
import { Seo } from "@/components/Seo";

export default function CampaignShare() {
  const { token } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/public/campaigns/${token}`)
      .then(({ data }) => setCampaign(data))
      .catch(() => setError(true));
  }, [token]);

  if (error)
    return (
      <div className="min-h-screen bg-background grain-overlay flex flex-col items-center justify-center gap-4 px-6 text-center">
        <img src="/nox-logo.png" alt="Nox AI" className="h-14 w-14 object-contain" />
        <h1 className="font-heading text-2xl font-black">Campaign not found</h1>
        <p className="text-muted-foreground">This share link is invalid or was removed.</p>
        <Link to="/">
          <Button className="bg-primary text-black hover:bg-[#00C2D9] font-semibold">Go to Nox AI</Button>
        </Link>
      </div>
    );

  if (!campaign)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <Seo
        title={campaign.name}
        description={`${campaign.name} — an AI-generated creative campaign made with Nox AI. View the images, videos and voiceovers, then create your own.`}
        path={`/c/${token}`}
        image={campaign.assets?.find((a) => a.type === "image")?.urls?.[0] || undefined}
        type="article"
      />
      <header className="sticky top-0 z-30 bg-[#050508]/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/nox-logo.png" alt="Nox AI" className="h-9 w-9 object-contain" />
            <span className="font-heading font-black text-lg tracking-tight">
              Nox<span className="text-primary"> AI</span>
            </span>
          </Link>
          <Link to="/register">
            <Button className="bg-primary text-black hover:bg-[#00C2D9] font-semibold" data-testid="share-cta">
              <Sparkles className="h-4 w-4 mr-2" /> Create your own
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="campaign-share-page">
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-primary mb-2">Shared campaign</p>
        <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter mb-8">{campaign.name}</h1>

        {campaign.assets?.length ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
            {campaign.assets.map((a, i) => {
              const url = a.urls?.[0];
              return (
                <div key={i} className="break-inside-avoid rounded-2xl overflow-hidden border border-[#222430] bg-[#0F1015]" data-testid="share-asset">
                  <div className="bg-black">
                    {a.type === "video" ? (
                      <video src={url} controls className="w-full" />
                    ) : a.type === "audio" ? (
                      <div className="p-6 flex flex-col items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center">
                          <Mic className="h-6 w-6 text-primary" />
                        </div>
                        <audio src={url} controls className="w-full" />
                      </div>
                    ) : (
                      <img src={url} alt={a.prompt} className="w-full" />
                    )}
                  </div>
                  <div className="p-4 flex items-center justify-between gap-3">
                    <p className="text-sm text-white/80 line-clamp-1">{a.prompt}</p>
                    {url && (
                      <a href={url} target="_blank" rel="noreferrer" download>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary">
                          <Download className="h-4 w-4" />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted-foreground">This campaign has no assets yet.</p>
        )}
      </main>
    </div>
  );
}
